import React from 'react';

/**
 * The Critical Timing Scan toolbar buttons — one per orb.
 *
 * <h2>Why three buttons and not one with a dialog</h2>
 * The orb is the study's dial, and the three settings are genuinely different readings of one rule
 * rather than three shades of the same answer. Measured across 1801-2100 they mark <b>4.4%</b>,
 * <b>15.3%</b> and <b>29.6%</b> of days. A reader comparing tolerances is comparing pictures, and a
 * dialog between each one turns that into a chore.
 *
 * <h2>Why its own module</h2>
 * So the table is data a test can read. TabPanel's routing test asserts that every study still
 * under evaluation is admin-gated, and it used to do that by grepping TabPanel.js for a line
 * carrying both `action:` and `adminOnly: true`. These entries are GENERATED, so those two fields
 * are on different lines and always will be — a line grep reports them as ungated when they are
 * not. Requiring TabPanel itself instead pulls its whole component tree into Jest, which will not
 * parse. A small data module is readable by both.
 */

/**
 * The orbs that get a button, and the tint that tells them apart.
 *
 * <p>These are exactly the orbs with a published dataset. That is not a coincidence: a button that
 * had to compute three centuries live would be a different feature.
 *
 * <p>Tinted light to dark with the orb, so the loosest reading is the heaviest mark on screen. The
 * badge carries the number; the tint is what makes three buttons scannable at 28px rather than
 * three identical triangles a reader has to squint at.
 */
export const CTS_ORB_BUTTONS = [
  { orb: 1, tint: '#ef9a9a' },
  { orb: 2, tint: '#e53935' },
  { orb: 3, tint: '#8e0000' },
];

/**
 * The ICONS entries for those buttons.
 *
 * <p>Generated rather than written out three times: the glyph, the tooltip and the action differ
 * only by the orb, and three hand-copied entries would drift the first time the icon changed.
 *
 * <p>The action carries the orb, because the ICONS array is module-level data whose entries hold no
 * state — the action string IS the parameter, and TabPanel reads it back off the string.
 */
export function ctsIconEntries() {
  return CTS_ORB_BUTTONS.map(({ orb, tint }) => ({
    component: (
      // A triangle over a bar — "trine plus a hard aspect, marked on a bar" — with the orb as a
      // badge. Drawn rather than imported so it stays crisp and takes these tints.
      <svg width="28" height="28" viewBox="0 0 28 28" aria-label={`CTS orb ${orb}`}>
        <path d="M13 3 L21 17 L5 17 Z" fill="none" stroke={tint}
          strokeWidth="1.8" strokeLinejoin="round" />
        <path d="M8 21 L18 21" stroke="#455a64" strokeWidth="1.7" strokeLinecap="round" />
        <path d="M13 17 L13 21" stroke="#455a64" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="21.5" cy="22" r="6" fill={tint} />
        <text x="21.5" y="22" textAnchor="middle" dominantBaseline="central"
          fontSize="9" fontWeight="700" fill="#fff">{orb}</text>
      </svg>
    ),
    alt: `CTS ${orb}`,
    /*
     * ⚠ `CTS1` / `CTS2` / `CTS3` — a REVERSAL, on instruction.
     *
     * This read `Critical Timing Scan - ${orb}`, on the reasoning that "CTS" alone says nothing
     * to a reader meeting it for the first time and the badge's digit needs the name beside it to
     * mean anything. That reasoning still describes a real cost, and it is now outweighed: on
     * integralDelta all three orbs sit together on the bar, where three hovers reading
     * "Critical Timing Scan - 1/2/3" are three long strings distinguished by their last
     * character. The short form is what makes a row of three scannable.
     */
    tooltip: `CTS${orb}`,
    action: `cts_scan_${orb}`,
    adminOnly: true,
  }));
}

/** The orb a `cts_scan_N` action asks for, or null if the action is not one of ours. */
export function orbForAction(action) {
  const match = /^cts_scan_(\d+)$/.exec(String(action || ''));
  if (!match) return null;
  const orb = Number(match[1]);
  return CTS_ORB_BUTTONS.some(b => b.orb === orb) ? orb : null;
}
